import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { fetchExperiencePricing, fetchSafariPolicies } from "../api/experienceApi";

const normalize = (value) => String(value || "").trim().toLowerCase();

export const buildPricingKey = (experienceType, identifier) => {
  const type = normalize(experienceType || "safari");
  const id = String(identifier || "").trim();
  return `${type}|${id}`;
};

export const buildPolicyKey = (destinationSlug, experienceSlug, vehicleId = "") => {
  const destination = normalize(destinationSlug);
  const experience = normalize(experienceSlug);
  const vehicle = normalize(vehicleId || "default");
  return `${destination}|${experience}|${vehicle}`;
};

export const loadExperiencePricing = createAsyncThunk(
  "experience/loadPricing",
  async ({ experienceType, identifier, payload }, { rejectWithValue }) => {
    if (!experienceType || !identifier) {
      return rejectWithValue("Experience type and identifier are required");
    }
    try {
      const res = await fetchExperiencePricing(experienceType, identifier, payload);
      const data = res && typeof res === "object" ? (res.data && typeof res.data === "object" ? res.data : res) : {};
      const starting = data.starting || {};
      const startingPricing = starting.pricing || data.pricing || {};
      const totals =
        data.totals ||
        data.pricing?.totals ||
        starting.totals ||
        starting.pricing?.totals ||
        {};
      const grandTotal = Number(
        totals?.grandTotal ??
          totals?.totalAmount ??
          data?.grandTotal ??
          starting?.totals?.grandTotal ??
          starting?.pricing?.totals?.grandTotal ??
          starting?.grandTotal,
      );
      return {
        summary: {
          amount: Number.isFinite(grandTotal) ? grandTotal : null,
          taxTotal: Number(totals?.taxTotal ?? starting?.taxTotal ?? startingPricing?.totals?.taxTotal) || 0,
          baseFare: Number(totals?.baseFare ?? starting?.baseFare ?? startingPricing?.totals?.baseFare) || null,
          travelDate: data.travelDate || starting.travelDate || payload?.date || null,
          vehicleLabel:
            startingPricing?.vehicleLabel ||
            startingPricing?.vehicle?.label ||
            starting?.vehicleLabel ||
            starting?.vehicle?.label ||
            starting?.vehicleId ||
            null,
          totals,
        },
        raw: data,
      };
    } catch (error) {
      const responseData = error?.data || error?.response?.data;
      const message = responseData?.message || error?.message || "Unable to load pricing";
      return rejectWithValue({
        message,
        status: error?.status || error?.response?.status,
        data: responseData,
      });
    }
  },
);

export const loadExperiencePolicies = createAsyncThunk(
  "experience/loadPolicies",
  async ({ destinationSlug, experienceSlug, vehicleId }, { rejectWithValue }) => {
    if (!destinationSlug || !experienceSlug) {
      return rejectWithValue("Destination and experience slugs are required");
    }
    try {
      const res = await fetchSafariPolicies(destinationSlug, experienceSlug, { vehicleId });
      const combinedPolicies = {
        ...(res?.policies || {}),
        dynamicPricingPolicy: res?.dynamicPricingPolicy ?? null,
        guestPolicy: res?.guestPolicy ?? null,
        foodPolicy: res?.foodPolicy ?? null,
        checkinPolicy: res?.checkinPolicy ?? null,
        policySource: res?.policySource ?? null,
        effectiveVehicle: res?.effectiveVehicle ?? null,
        vehiclePolicies: res?.vehiclePolicies ?? [],
      };
      const hasCombinedPolicies = [
        combinedPolicies.cancellation,
        combinedPolicies.dateChange,
        ...(Array.isArray(combinedPolicies.transactionCharge) ? combinedPolicies.transactionCharge : []),
        combinedPolicies.gstSlab,
        combinedPolicies.divisionalSplit,
        combinedPolicies.dynamicPricingPolicy,
        combinedPolicies.guestPolicy,
        combinedPolicies.foodPolicy,
        combinedPolicies.checkinPolicy,
      ].some(Boolean);
      return {
        policies: hasCombinedPolicies ? combinedPolicies : null,
        bookingPolicy: res?.bookingPolicy || null,
        raw: res || null,
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || "Unable to load policies");
    }
  },
);

const initialState = {
  pricing: {},
  policies: {},
  configs: {},
};

const upsertEntry = (collection, key, changes) => {
  // eslint-disable-next-line no-param-reassign
  collection[key] = {
    status: changes.status || "idle",
    requestId: changes.requestId || collection[key]?.requestId || null,
    data: changes.data ?? null,
    raw: changes.raw ?? collection[key]?.raw ?? null,
    bookingPolicy: changes.bookingPolicy ?? collection[key]?.bookingPolicy ?? null,
    error: changes.error ?? null,
    fetchedAt: changes.fetchedAt ?? collection[key]?.fetchedAt ?? null,
  };
};

const experienceSlice = createSlice({
  name: "experience",
  initialState,
  reducers: {
    setDraftConfig: (state, action) => {
      const { key, config } = action.payload || {};
      if (!key) return;
      state.configs[key] = { ...(state.configs[key] || {}), ...(config || {}) };
    },
    clearDraftConfig: (state, action) => {
      const key = action.payload;
      if (key && state.configs[key]) {
        delete state.configs[key];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadExperiencePricing.pending, (state, action) => {
        const key = buildPricingKey(action.meta.arg.experienceType, action.meta.arg.identifier);
        upsertEntry(state.pricing, key, {
          status: "loading",
          requestId: action.meta.requestId,
          data: state.pricing[key]?.data || null,
          error: null,
        });
      })
      .addCase(loadExperiencePricing.fulfilled, (state, action) => {
        const key = buildPricingKey(action.meta.arg.experienceType, action.meta.arg.identifier);
        if (state.pricing[key]?.requestId !== action.meta.requestId) return;
        upsertEntry(state.pricing, key, {
          status: "succeeded",
          data: action.payload.summary,
          raw: action.payload.raw,
          error: null,
          fetchedAt: Date.now(),
        });
      })
      .addCase(loadExperiencePricing.rejected, (state, action) => {
        const key = buildPricingKey(action.meta.arg.experienceType, action.meta.arg.identifier);
        if (state.pricing[key]?.requestId !== action.meta.requestId) return;
        const payload = action.payload;
        const errorMessage =
          (payload && typeof payload === "object" ? payload.message : payload) ||
          action.error?.message ||
          "Unable to load pricing";
        upsertEntry(state.pricing, key, {
          status: "failed",
          error: errorMessage,
          raw: payload && typeof payload === "object" ? payload.data : undefined,
        });
      })
      .addCase(loadExperiencePolicies.pending, (state, action) => {
        const key = buildPolicyKey(
          action.meta.arg.destinationSlug,
          action.meta.arg.experienceSlug,
          action.meta.arg.vehicleId,
        );
        upsertEntry(state.policies, key, {
          status: "loading",
          error: null,
        });
      })
      .addCase(loadExperiencePolicies.fulfilled, (state, action) => {
        const key = buildPolicyKey(
          action.meta.arg.destinationSlug,
          action.meta.arg.experienceSlug,
          action.meta.arg.vehicleId,
        );
        upsertEntry(state.policies, key, {
          status: "succeeded",
          data: action.payload.policies,
          raw: action.payload.raw,
          bookingPolicy: action.payload.bookingPolicy || state.policies[key]?.bookingPolicy || null,
          error: null,
          fetchedAt: Date.now(),
        });
      })
      .addCase(loadExperiencePolicies.rejected, (state, action) => {
        const key = buildPolicyKey(
          action.meta.arg.destinationSlug,
          action.meta.arg.experienceSlug,
          action.meta.arg.vehicleId,
        );
        upsertEntry(state.policies, key, {
          status: "failed",
          error: action.payload || action.error?.message || "Unable to load policies",
        });
      });
  },
});

const selectExperienceState = (state) => state.experience || initialState;

const EMPTY_ENTRY = { status: "idle", data: null, raw: null, bookingPolicy: null, error: null };

const selectEntry = (collection, key) => collection[key] || EMPTY_ENTRY;

export const selectPricingEntry = (state, key) => selectEntry(selectExperienceState(state).pricing, key);
export const selectPoliciesEntry = (state, key) => selectEntry(selectExperienceState(state).policies, key);
export const selectDraftConfig = (state, key) => selectExperienceState(state).configs[key] || null;
export const { setDraftConfig, clearDraftConfig } = experienceSlice.actions;

export const makePricingSelector = (key) =>
  createSelector(
    (state) => selectPricingEntry(state, key),
    (entry) => entry,
  );

export const makePoliciesSelector = (key) =>
  createSelector(
    (state) => selectPoliciesEntry(state, key),
    (entry) => entry,
  );

export default experienceSlice.reducer;
